import { useMemo, useState } from "react";
import { maHranu } from "@/lib/oznacenieRiadkov";
import { useUpdateDielec } from "@/api/hooks/useDielce";
import { useZakazkaDetail } from "@/api/hooks/useZakazky";
import { VyberHranyDialog } from "@/components/dielec/VyberHranyDialog";
import { VyberKovaniaDialog } from "@/components/dielec/VyberKovaniaDialog";
import { jePredvolenyNazovKovania, predvolenyNazovKovania } from "@/lib/kategorie";
import { pridaneHranyNaVstup } from "@/lib/pridaneHrany";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { sklonuj, toNum } from "@/lib/format";
import type { Dielec, DielecUpdate, MaterialSlot, Varianta, Zostava } from "@/api/schemas";

/**
 * Hromadná výmena hrany alebo kovania naprieč dielcami zákazky
 * (scope = varianta + voliteľná zostava, rovnako ako sumár, z ktorého
 * sa dialóg otvára). Hrana sa vyberá v tom istom okne ako všade
 * (`VyberHranyDialog`, KOLO 201): odporúčané sú hrany dosky dielcov,
 * ktoré starú hranu majú, a výmenu spustí až tlačidlo „Vymeniť v N
 * dielcoch" — do KOLA 201 ju spustil prvý klik do zoznamu. Kovanie
 * rovnako (`VyberKovaniaDialog`, KOLO 202) — okno začne v kategórii
 * starého kovania.
 *
 * Hrana sa od KOLA 169 míňa aj mimo strán — cenovka z hrany (jej
 * materiál) a pridané hrany dielca (olepenie otvoru). Sumár ich ráta
 * spolu so stranami, takže výmena ich musí vymeniť tiež, inak by stará
 * hrana v sumári ostala.
 */
export interface VymenaStary {
  id: string;
  nazov: string;
  dekor_kod?: string | null;
  kategoria?: string | null;
  /** Alias kovania — predvolený názov riadku (KOLO 176) */
  alias?: string | null;
}

export function VymenaDialog({
  rezim,
  zakazkaId,
  variantaId,
  zostavaId,
  stary,
  open,
  onOpenChange,
}: {
  rezim: "hrana" | "kovanie";
  zakazkaId: string;
  variantaId: string;
  zostavaId?: string;
  stary: VymenaStary;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const zakazkaQuery = useZakazkaDetail(zakazkaId);
  const updateDielec = useUpdateDielec(zakazkaId);
  const [busy, setBusy] = useState(false);

  // Dielce v scope sumára (varianta + voliteľná zostava), bez zmazaných
  const { dielce, sloty } = useMemo(() => {
    const varianty = (zakazkaQuery.data?.data?.varianty ?? []) as Varianta[];
    const varianta = varianty.find((v) => v.id === variantaId);
    const zostavy = ((varianta?.zostavy ?? []) as Zostava[]).filter(
      (z) => !zostavaId || z.id === zostavaId,
    );
    return {
      dielce: zostavy.flatMap((z) => (z.dielce ?? []) as Dielec[]),
      sloty: (varianta?.sloty ?? []) as MaterialSlot[],
    };
  }, [zakazkaQuery.data, variantaId, zostavaId]);

  // Dielce so starou hranou — počet do tlačidla a doska, podľa ktorej
  // sa odporúča náhrada (najčastejší materiál a hrúbka medzi nimi)
  const sHranou = useMemo(
    () => (rezim === "hrana" ? dielce.filter((d) => maHranu(d, stary.id)) : []),
    [rezim, dielce, stary.id],
  );
  const sKovanim = useMemo(
    () => (rezim === "kovanie" ? dielce.filter((d) => d.material_id_direct === stary.id) : []),
    [rezim, dielce, stary.id],
  );
  const slot = najcastejsi(
    sHranou.map((d) => sloty.find((s) => s.id === d.material_slot_id)).filter(
      (s): s is MaterialSlot => s !== undefined,
    ),
    (s) => s.id,
  );
  const hrubka = najcastejsi(
    sHranou.filter((d) => !d.riadok_z_hrany && d.z_mm != null).map((d) => toNum(d.z_mm)),
    (z) => String(z),
  );

  async function vymen(
    novyId: string,
    novyNazov: string,
    novaKategoria?: string | null,
    novyAlias?: string | null,
  ): Promise<void> {
    if (novyId === stary.id) {
      onOpenChange(false);
      return;
    }
    setBusy(true);
    try {
      let zmenenych = 0;
      for (const d of dielce) {
        const data: DielecUpdate = {};
        if (rezim === "hrana") {
          for (const strana of ["x1", "x2", "y1", "y2"] as const) {
            const idKey = `hrana_${strana}_id` as const;
            const autoKey = `hrana_${strana}_auto` as const;
            if ((d as Record<string, unknown>)[idKey] === stary.id) {
              (data as Record<string, unknown>)[idKey] = novyId;
              // manuálny výber — auto-resolve ho nesmie prepísať
              (data as Record<string, unknown>)[autoKey] = false;
            }
          }
          // Cenovka z hrany — hrana je jej materiál
          if (d.riadok_z_hrany && d.material_id_direct === stary.id) {
            data.material_id_direct = novyId;
          }
          // Pridané hrany — PATCH nahrádza celý zoznam, ide celý
          const pridane = d.pridane_hrany ?? [];
          if (pridane.some((h) => h.material_id === stary.id)) {
            data.pridane_hrany = pridaneHranyNaVstup(
              pridane.map((h) =>
                h.material_id === stary.id ? { ...h, material_id: novyId } : h,
              ),
            );
          }
        } else if (d.material_id_direct === stary.id) {
          data.material_id_direct = novyId;
          // Predvolený názov (alias, kategória, pravý názov) prepíš podľa
          // nového kovania — vzor KovanieChangeDialog (KOLO 176)
          if (jePredvolenyNazovKovania(d.nazov_strojovy, stary)) {
            data.nazov_strojovy = predvolenyNazovKovania({
              nazov: novyNazov,
              alias: novyAlias,
              kategoria: novaKategoria,
            });
          }
        }
        if (Object.keys(data).length > 0) {
          await updateDielec.mutateAsync({ id: d.id, data });
          zmenenych += 1;
        }
      }
      toast({
        title: rezim === "hrana" ? "Hrana vymenená" : "Kovanie vymenené",
        description: `${stary.nazov} → ${novyNazov} (${zmenenych} dielcov)`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Výmena zlyhala",
        description: extractErrorMessage(err),
      });
    } finally {
      setBusy(false);
    }
  }

  const rozsah = zostavaId ? "v zostave" : "v zákazke";
  const n = rezim === "hrana" ? sHranou.length : sKovanim.length;
  const vymenText = busy
    ? "Vymieňam…"
    : n > 0
      ? `Vymeniť v ${n} ${sklonuj(n, ["dielci", "dielcoch", "dielcoch"])}`
      : "Vymeniť";
  if (rezim === "hrana") {
    return (
      <VyberHranyDialog
        open={open}
        onClose={() => onOpenChange(false)}
        currentId={null}
        nadpis={`Vymeniť hranu — ${stary.nazov}`}
        popis={`Výmena sa aplikuje na všetky dielce ${rozsah}, ktoré túto hranu používajú — strany, cenovky z hrany aj pridané hrany.`}
        potvrdText={vymenText}
        busy={busy}
        slotMaterialId={slot?.material_id}
        slotId={slot?.id}
        zMm={hrubka ?? undefined}
        variantaId={variantaId}
        bezKuracie
        vynechat={[stary.id]}
        onSelect={(id, nazov) => void vymen(id, nazov ?? "nová hrana")}
      />
    );
  }

  return (
    <VyberKovaniaDialog
      open={open}
      onClose={() => onOpenChange(false)}
      nadpis={`Vymeniť kovanie — ${stary.nazov}`}
      popis={`Výmena sa aplikuje na všetky dielce ${rozsah}, ktoré toto kovanie majú.`}
      potvrdText={vymenText}
      busy={busy}
      kategoriaPodla={stary.id}
      vynechat={[stary.id]}
      onSelect={(m) => void vymen(m.id, m.nazov, m.kategoria, m.alias)}
    />
  );
}

/** Najčastejšia hodnota (pri zhode prvá); prázdny zoznam = `null`. */
function najcastejsi<T>(hodnoty: readonly T[], kluc: (h: T) => string): T | null {
  const pocty = new Map<string, { h: T; n: number }>();
  for (const h of hodnoty) {
    const k = kluc(h);
    const z = pocty.get(k);
    if (z) z.n += 1;
    else pocty.set(k, { h, n: 1 });
  }
  let naj: { h: T; n: number } | null = null;
  for (const z of pocty.values()) if (!naj || z.n > naj.n) naj = z;
  return naj?.h ?? null;
}
